import React from 'react';
import { GridColumnHeaderTitle } from '@mui/x-data-grid-pro';
import Tooltip from '@mui/material/Tooltip';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Checkbox from '@mui/material/Checkbox';
import { blue } from '@mui/material/colors';
import PhotoCell from '../Photos/PhotoCell';
import { TimeAdjustmentCell } from '../CaseReview';
import { getCompAdjustedPriceWithTooltip } from '../../../utils/adjustmentCalculator';
import AdjustmentCell from '../common/AdjustmentCell';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styleOptions, viewOptions, conditionOptions, gradeOptions, CASE_REVIEW_COLUMNS } from '../../../common/mappings';
import { getTrainingColor } from './caseReviewGridStyles';
import logger from '../../../utils/logger';
import { normalizeCompId } from '../common/compSheetLookup';
import { calculateCalculatedTav } from '../utils/calculatedTav';
import { parseCalendarDate } from '../../../common/dateUtils';

/** Sheet index + misc-adj identity (TransactionID on sales, parcel otherwise). */
const sheetIndexAndCompId = (params, row) => {
  const parcelID = row?.parcel_id;
  const txn = row?.TransactionID;
  const memoizedCompObject = params.memoizedCompObject || {};
  const currentCompId = params.IS_SALE_RUN && txn != null && txn !== '' ? txn : parcelID;
  let parcelIndex = -1;
  if (memoizedCompObject.parcel_id) {
    if (params.IS_SALE_RUN && txn != null && txn !== '' && memoizedCompObject.TransactionID) {
      const needle = normalizeCompId(txn);
      parcelIndex = memoizedCompObject.TransactionID.findIndex((id) => normalizeCompId(id) === needle);
    }
    if (parcelIndex < 0) parcelIndex = memoizedCompObject.parcel_id.indexOf(parcelID);
  }
  return { parcelID, currentCompId, parcelIndex, memoizedCompObject };
};

// Custom checkbox components
const CustomCheckboxHeader = React.memo(({ onChange, hasSelectedComps }) => {
  const showCheckbox = hasSelectedComps;

  return (
    <div
      className={`items-center justify-center content-center m-auto flex h-full w-full ${showCheckbox ? '' : ''}`}
      style={{
        width: 22,
        height: 22,
      }}
    >
      {showCheckbox ?
        <Checkbox
          checked={false}
          indeterminate={hasSelectedComps}
          onChange={onChange}
          icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 24 }} />}
          sx={{
            '& .MuiSvgIcon-root': {
              fontSize: 26,
            },
          }}
        />
        :
        <div className='w-[10px]'>#</div>
      }
    </div>
  );
}, (prevProps, nextProps) => prevProps.hasSelectedComps === nextProps.hasSelectedComps);

const CustomCheckboxCell = React.memo(({ checked, selectedRowNumber, trainingSelectedNumber = 0, trainingUserOrdinal = 0, rowIndex }) => {
  const isUserSelected = selectedRowNumber > 0;
  const isTrainingSelected = !isUserSelected && trainingSelectedNumber > 0;
  const number = isUserSelected ? selectedRowNumber : (isTrainingSelected ? trainingSelectedNumber : '');
  const displayNumber = rowIndex; // Always show the row index number

  return (
    <div 
      className='flex items-center h-full' 
      style={{ 
        paddingLeft: '8px',
        paddingRight: '8px',
        minHeight: '52px' // Ensure consistent row height
      }}
    >
      <div style={{ 
        width: '24px', 
        height: '24px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        marginRight: '8px'
      }}>
        <Checkbox
          icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 24 }} />}
          checkedIcon={
            <div style={{
              position: 'relative',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isUserSelected ? blue[700] : getTrainingColor(trainingUserOrdinal),
              borderRadius: '10%',
            }}>
              <span style={{
                position: 'absolute',
                fontSize: 16,
                color: '#fff',
                fontWeight: 'bold',
              }}>
                {number}
              </span>
            </div>
          }
          checked={isUserSelected || isTrainingSelected}
          sx={{
            padding: 0,
            margin: 0
          }}
        />
      </div>
      <span 
        className='text-black font-medium text-sm'
        style={{
          lineHeight: '24px', // Match checkbox height
          minWidth: '20px', // Ensure consistent width for numbers
          textAlign: 'left'
        }}
      >
        {displayNumber}
      </span>
    </div>
  );
});

// Helper functions
const imputedValueCheck = (value) => {
  if (typeof value === 'string' && value.includes('*')) {
    return (
      <div style={{ color: '#d32f2f' }} className='h-full w-full flex items-center justify-center'>
        <span className='text-red-500 h-full w-full'>{value}</span>
      </div>
    );
  }
  return value;
};

// Sorting helper for numeric fields that may contain imputed markers (e.g. "2,200*" or "15*").
// Returns a number for valid values, otherwise null (so empty/imputed-unparseable values sort last).
const parseImputedNumericForSort = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const str = value.trim();
    if (str === '') return null;
    const cleaned = str.replace(/\*/g, '').replace(/,/g, '');
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const imputedNumericSortComparator = (v1, v2) => {
  const n1 = parseImputedNumericForSort(v1);
  const n2 = parseImputedNumericForSort(v2);

  if (n1 === null && n2 === null) return 0;
  if (n1 === null) return 1;
  if (n2 === null) return -1;
  return n1 - n2;
};

const handleNumericValue = (value, defaultValue = '') => {
  if (value === 0 || value === '0') {
    return 0;
  }
  return value || defaultValue;
};

const parseAdjustmentBaseValue = (value) => {
  const numericValue = Number(String(value ?? '').replace(/[,$]/g, ''));
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
};

const calculateAdjustmentPercentages = (result, baseValue) => {
  if (!result || !baseValue) {
    return { grossAdjPct: null, netAdjPct: null };
  }
  const rawTotal = Object.values(result.rawAdjustments || {}).reduce(
    (sum, adjustment) => sum + (Number(adjustment) || 0),
    0
  );
  const miscTotal = (result.miscAdjustmentTooltips || []).reduce(
    (sum, adjustment) => sum + (Number(adjustment?.adjustment) || 0),
    0
  );
  const grossRawSum = Object.values(result.rawAdjustments || {}).reduce(
    (sum, adjustment) => sum + Math.abs(Number(adjustment) || 0),
    0
  );
  const grossMiscSum = (result.miscAdjustmentTooltips || []).reduce(
    (sum, adj) => sum + Math.abs(Number(adj?.adjustment) || 0),
    0
  );
  const grossAdjPct = ((grossRawSum + grossMiscSum) / baseValue) * 100;
  const netAdjPct = ((rawTotal + miscTotal) / baseValue) * 100;
  return {
    grossAdjPct: Number.isFinite(grossAdjPct) ? grossAdjPct : null,
    netAdjPct: Number.isFinite(netAdjPct) ? netAdjPct : null,
  };
};

// Style, view, condition, and grade options are now imported from mappings.js

// Column order configuration - derived from centralized CASE_REVIEW_COLUMNS.
// To add a new CaseReview column, update CASE_REVIEW_COLUMNS in mappings.js.
export const COLUMN_ORDER = [
  'actions',
  ...CASE_REVIEW_COLUMNS.map((c) => c.field),
];

// Individual column definitions factory
const createColumnDefinitionsObject = (params) => ({
  // Checkbox selection (action col type)
  actions: {
    field: 'actions',
    type: 'actions',
    hide: true,
    width: 65,
    headerClassName: 'actions',
    disableColumnMenu: true,
    handleSelectAllChange: params.handleSelectAllChange, // Store the function in the column definition
    renderHeader: (headerParams) => {
      const handleChange = () => {
        headerParams.colDef.handleSelectAllChange?.(headerParams);
      };
      return (
        <CustomCheckboxHeader
          hasSelectedComps={!!params.hasSelectedComps}
          onChange={handleChange}
        />
      );
    },
    renderCell: (params) => {
      return (
        <CustomCheckboxCell
          checked={params.row.selected}
          selectedRowNumber={params.row.selectedRowNumber}
          trainingSelectedNumber={params.row.trainingSelectedNumber}
          trainingUserOrdinal={params.row.trainingUserOrdinal}
          rowIndex={params.row['#']}
        />
      );
    },
  },

  // Address
  Address: {
    editable: false,
    disableColumnMenu: true,
    field: "Address",
    headerName: "Address",
    type: 'string',
    align: "left",
    hide: false,
    flex: 1.5,
    renderCell: (cellParams) => {
      const address = cellParams.row.PropertyInfo?.Address || cellParams.value;
      if (!address) return '';
      
      return (
        <div className="text-blue-800 hover:text-blue-900 cursor-pointer underline"
             onClick={() => params.handleAddressClick(cellParams.row)}>
          {address}
        </div>
      );
    },
   
    filterable: true,
  },

  // Photo
  PhotoURL: {
    editable: false,
    disableColumnMenu: true,
    field: "PhotoURL",
    headerName: "Photo",
    sortable: false,
    filterable: false,
    minWidth: 120,
    flex: 0.8,
    cellClassName: 'photo-cell-no-padding',
    disableClickEventBubbling: true,
    renderCell: (params) => {
      return (
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '0px !important', 
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <PhotoCell
            parcelId={params.row?.parcel_id || params.row?.PID || params.row?.ParcelID || params.row?.id}
            photoUrl={params.row?.PhotoURL}
            MLSPhotoFolder={params.row?.MLSPhotoFolder}
            cellMode="edit"
            showUploadPrompt={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    },
  },

  // Town
  Town: {
    editable: false,
    disableColumnMenu: true,
    field: "Town",
    headerName: "Town",
    type: 'string',
    align: "left",
    hide: true,
    flex: 0.6,
  },

  // SDName
  SDName: {
    editable: false,
    disableColumnMenu: true,
    field: "SDName",
    headerName: "SD Name",
    type: 'string',
    align: "left",
    flex: 0.6,
  },

  // SDCode
  SDCode: {
    editable: false,
    disableColumnMenu: true,
    field: "SDCode",
    headerName: "SD Code",
    type: 'string',
    align: "left",
    hide: true,
    flex: 0.5,
  },

  // ZipCode
  ZipCode: {
    editable: false,
    disableColumnMenu: true,
    field: "ZipCode",
    headerName: "Zip",
    type: 'string',
    align: "left",
    hide: true,
    flex: 0.5,
  },

  // NeighborhoodCode
  NeighborhoodCode: {
    editable: true,
    disableColumnMenu: true,
    field: "NeighborhoodCode",
    headerName: "Neighborhood",
    type: 'string',
    align: "left",
    hide: true,
    flex: 0.5,
  },

  // parcel_id
  parcel_id: {
    editable: false,
    disableColumnMenu: true,
    field: "parcel_id",
    headerName: "PID",
    type: 'string',
    align: "left",
    hide: true,
    flex: 0.9,
  },

  // PCC
  PCC: {
    editable: false,
    disableColumnMenu: true,
    field: "PCC",
    headerName: "PCC",
    type: "string",
    flex: 0.2,
  },

  // Style
  Style: {
    editable: true,
    disableColumnMenu: true,
    field: "Style",
    headerName: "Style",
    type: 'singleSelect',
    align: "left",
    valueOptions: styleOptions,
    hide: false,
    flex: 0.8,
    renderCell: (params) => {
      if (params.value === 'Unknown') {
        return (
          <div style={{ color: '#d32f2f' }} className='h-full w-full flex items-center justify-center'>
            <span className='text-red-500 h-full w-full'>Unknown*</span>
          </div>
        );
      }
      return params.value;
    },
  },

  // View
  View: {
    editable: true,
    disableColumnMenu: true,
    field: "View",
    headerName: "View",
    type: 'singleSelect',
    align: "left",
    valueOptions: viewOptions,
    hide: false,
    flex: 0.7,
  },

  // YearBuilt
  YearBuilt: {
    editable: true,
    disableColumnMenu: true,
    field: "YearBuilt",
    headerName: "Yr",
    type: "number",
    flex: 0.5,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => {
      try {
        if (typeof value === 'string' && value.includes('*')) return value;
        const parsedValue = Number(value);
        if (!isNaN(parsedValue)) {
          const [integerPart, decimalPart] = parsedValue.toString().split('.');
          if (decimalPart && decimalPart.length > 4) {
            return `${parsedValue.toFixed(0)}*`;
          }
          return integerPart;
        }
      } catch (error) {
        logger.error('Error processing value:', value, error);
      }
    },
    renderCell: (params) => imputedValueCheck(params.value),
  },

  // EffYearBuilt
  EffYearBuilt: {
    editable: true,
    disableColumnMenu: true,
    field: "EffYearBuilt",
    headerName: "Eff Yr",
    type: "number",
    flex: 0.5,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => {
      if (value === null || value === undefined || value === '') return null;
      try {
        if (typeof value === 'string' && value.includes('*')) return value;
        const parsedValue = Number(value);
        if (!isNaN(parsedValue)) {
          const [integerPart, decimalPart] = parsedValue.toString().split('.');
          if (decimalPart && decimalPart.length > 4) {
            return `${parsedValue.toFixed(0)}*`;
          }
          return integerPart;
        }
      } catch (error) {
        logger.error('Error processing value:', value, error);
      }
    },
    renderCell: (params) => imputedValueCheck(params.value),
  },

  // Sqft
  Sqft: {
    editable: true,
    disableColumnMenu: true,
    field: "Sqft",
    headerName: "Sqft",
    type: "number",
    hide: false,
    flex: 0.6,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => {
      if (typeof value === 'string' && value.includes('*')) return value;
      if(value === '') return '';
      const parsedValue = Number(value);
      if (!isNaN(parsedValue)) {
        const formattedValue = Math.round(parsedValue).toLocaleString()
        const [integerPart, decimalPart] = parsedValue.toString().split('.');
        if (decimalPart && decimalPart.length >= 2) {
          return `${formattedValue}*`;
        }
        return parsedValue;
      }
      return '';
    },
    renderCell: (params) => {
      if (typeof params.value === 'number') return params.value.toLocaleString();
      return imputedValueCheck(params.value);
    },
  },

  // EstSqft
  EstSqft: {
    editable: false,
    disableColumnMenu: true,
    field: "EstSqft",
    headerName: "Est. Sqft",
    type: "number",
    hide: false,
    flex: 0.6,
    renderHeader: (params) => (
      <Tooltip title="Propriety's internally calculated sqft best estimate">
        <span>
          <GridColumnHeaderTitle label="Est. Sqft" columnWidth={params.colDef.computedWidth} />
        </span>
      </Tooltip>
    ),
    sortComparator: imputedNumericSortComparator,
    renderCell: (params) => {
      if (typeof params.value === 'number') return params.value.toLocaleString();
      return imputedValueCheck(params.value);
    },
    valueGetter: (value) => {
      if (typeof value === 'string' && value.includes('*')) return value;
      if(value === '') return '';
      const parsedValue = Number(value);
      if (!isNaN(parsedValue)) {
        const formattedValue = Math.round(parsedValue).toLocaleString()
        const [integerPart, decimalPart] = parsedValue.toString().split('.');
        if (decimalPart && decimalPart.length >= 2) {
          return `${formattedValue}*`;
        }
        return parsedValue;
      }
      return '';
    },
  },

  // RegridBldgFtPrnt
  RegridBldgFtPrnt: {
    editable: false,
    disableColumnMenu: true,
    field: "RegridBldgFtPrnt",
    headerName: "Regrid Sqft",
    type: "number",
    hide: false,
    flex: 0.6,
    sortComparator: imputedNumericSortComparator,
    renderCell: (params) => {
      if (typeof params.value === 'number') return params.value.toLocaleString();
      return imputedValueCheck(params.value);
    },
    valueGetter: (value) => {
      if (typeof value === 'string' && value.includes('*')) return value;
      if (value === '') return '';
      const parsedValue = Number(value);
      if (!isNaN(parsedValue)) {
        const formattedValue = Math.round(parsedValue).toLocaleString();
        const [, decimalPart] = parsedValue.toString().split('.');
        if (decimalPart && decimalPart.length >= 2) {
          return `${formattedValue}*`;
        }
        return parsedValue;
      }
      return '';
    },
  },

  // RegridConvertedSqFt
  // RegridConvertedSqFt: {
  //   editable: false,
  //   disableColumnMenu: true,
  //   field: "RegridConvertedSqFt",
  //   headerName: "Regrid Converted",
  //   type: "number",
  //   hide: false,
  //   flex: 0.6,
  //   sortComparator: (v1, v2) => {
  //     const n1 = Number(String(v1).replace(/[,*]/g, ''));
  //     const n2 = Number(String(v2).replace(/[,*]/g, ''));
  //     return n1 - n2;
  //   },
  //   valueGetter: (value) => {
  //     if (typeof value === 'string' && value.includes('*')) return value;
  //     if(value === '') return '';
  //     const parsedValue = Number(value);
  //     if (!isNaN(parsedValue)) {
  //       const formattedValue = Math.round(parsedValue).toLocaleString()
  //       const [integerPart, decimalPart] = parsedValue.toString().split('.');
  //       if (decimalPart && decimalPart.length >= 2) {
  //         return `${formattedValue}*`;
  //       }
  //       return parsedValue;
  //     }
  //     return '';
  //   },
  //   renderCell: (params) => imputedValueCheck(params.value),
  // },

  // Acres
  Acres: {
    editable: true,
    disableColumnMenu: true,
    field: "Acres",
    headerName: "Acres",
    type: "number",
    minWidth: 60,
    flex: 0.6,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => {
      if (typeof value === 'string' && value.includes('*')) return value;
      if(value === '') return '';
      const parsedValue = Number(value);
      if (!isNaN(parsedValue)) {
        const [integerPart, decimalPart] = parsedValue.toString().split('.');
        if (decimalPart && decimalPart.length > 4) {
          return `${parsedValue.toFixed(2)}*`;
        }
        return parsedValue.toFixed(2);
      }
      return '';
    },
    renderCell: (params) => imputedValueCheck(params.value),
  },

  // Grade
  Grade: {
    editable: true,
    disableColumnMenu: true,
    field: "Grade",
    headerName: "Grade",
    type: "singleSelect",
    valueOptions: gradeOptions,
    minWidth: 60,
    flex: 0.6,
    renderCell: (params) => {
      if (params.value === 'Unknown') {
        return (
          <div style={{ color: '#d32f2f' }} className='h-full w-full flex items-center justify-center'>
            <span className='text-red-500 h-full w-full'>Average*</span>
          </div>
        );
      }
      return params.value;
    },
  },

  // Condition
  Condition: {
    editable: true,
    disableColumnMenu: true,
    field: "Condition",
    headerName: "Condition",
    type: 'singleSelect',
    align: "left",
    valueOptions: conditionOptions,
    hide: false,
    flex: 0.7,
    renderCell: (params) => {
      if (params.value === 'Unknown') {
        return (
          <div style={{ color: '#d32f2f' }} className='h-full w-full flex items-center justify-center'>
            <span className='text-red-500 h-full w-full'>Average*</span>
          </div>
        );
      }
      return params.value;
    },
  },

  // ALFlag - NEW COLUMN
  ALFlag: {
    editable: false,
    disableColumnMenu: true,
    field: "ALFlag",
    headerName: "AL Flag",
    type: "number",
    align: "center",
    hide: false,
    flex: 0.4,
    renderCell: (params) => {
      const value = params.value;
      // Show red X for 0
      if (value === 0) {
        return (
          <div style={{ color: '#d32f2f' }} className='h-full w-full flex items-center justify-center'>
            <span className='text-red-500 font-bold'>✗</span>
          </div>
        );
      }
      // Show checkmark for 1
      if (value === 1) {
        return (
          <div style={{ color: '#2e7d32' }} className='h-full w-full flex items-center justify-center'>
            <span className='text-green-600 font-bold'>✓</span>
          </div>
        );
      }
      // Show blank for null, undefined, empty string, or any other value
      return (
        <div className='h-full w-full flex items-center justify-center'>
          <span></span>
        </div>
      );
    },
  },

  // Pool
  Pool: {
    editable: true,
    disableColumnMenu: true,
    field: "Pool",
    headerName: "IG Pool",
    type: "singleSelect",
    valueOptions: ['None', 'True'],
    minWidth: 60,
    align: 'center',
    headerAlign: 'center',
    flex: 0.6,
  },

  // FinishedBasement
  FinishedBasement: {
    editable: true,
    disableColumnMenu: true,
    field: "FinishedBasement",
    headerName: "Basement",
    type: "number",
    minWidth: 60,
    flex: 0.6,
  },

  // TotalGarageCap
  TotalGarageCap: {
    editable: true,
    disableColumnMenu: true,
    field: "TotalGarageCap",
    headerName: "Garage #",
    type: "number",
    minWidth: 60,
    flex: 0.6,
  },

  // Fireplaces
  Fireplaces: {
    editable: true,
    disableColumnMenu: true,
    field: "Fireplaces",
    headerName: "Fireplaces",
    type: "number",
    minWidth: 60,
    flex: 0.6,
  },

  // Deck
  Deck: {
    editable: true,
    disableColumnMenu: true,
    field: "Deck",
    headerName: "Deck",
    type: "number",
    minWidth: 60,
    flex: 0.6,
    valueGetter: (value) => (value == null || value === '' ? null : Number(value)),
  },

  // Patio
  Patio: {
    editable: true,
    disableColumnMenu: true,
    field: "Patio",
    headerName: "Patio",
    type: "number",
    minWidth: 60,
    flex: 0.6,
    valueGetter: (value) => (value == null || value === '' ? null : Number(value)),
  },

  // Porch
  Porch: {
    editable: true,
    disableColumnMenu: true,
    field: "Porch",
    headerName: "Porch",
    type: "number",
    minWidth: 60,
    flex: 0.6,
    valueGetter: (value) => (value == null || value === '' ? null : Number(value)),
  },

  // fullBaths
  fullBaths: {
    editable: true,
    disableColumnMenu: true,
    field: "fullBaths",
    headerName: "Full Baths",
    type: "number",
    flex: 0.6,
  },

  // halfBaths
  halfBaths: {
    editable: true,
    disableColumnMenu: true,
    field: "halfBaths",
    headerName: "Half Baths",
    type: "number",
    flex: 0.6,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => {
      // If value is actually 0, return 0 to display as "0"
      if (value === 0 || value === '0') {
        return 0;
      }
      return value;
    },
    renderCell: (params) => imputedValueCheck(params.value),
  },

  // totalBaths
  totalBaths: {
    editable: false,
    disableColumnMenu: true,
    field: "totalBaths",
    headerName: "Baths",
    type: "number",
    flex: 0.4,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => {
      if (typeof value === 'string' && value.includes('*')) return value;
      if(value == 0) return '0*';
      const parsedValue = Number(value);
      return parsedValue % 1 === 0 ? parsedValue.toString() : parsedValue.toFixed(1);
    },
    renderCell: (params) => imputedValueCheck(params.value),
  },

  // Kitchens
  Kitchens: {
    editable: true,
    disableColumnMenu: true,
    field: "Kitchens",
    headerName: "Kitchens",
    type: "number",
    flex: 0.6,
    minWidth: 60,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => handleNumericValue(value, ''),
    renderCell: (params) => imputedValueCheck(params.value),
  },

  // Bedrooms
  Bedrooms: {
    editable: true,
    disableColumnMenu: true,
    field: "Bedrooms",
    headerName: "Bedrooms",
    type: "number",
    flex: 0.5,
    minWidth: 60,
    sortComparator: imputedNumericSortComparator,
    valueGetter: (value) => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    },
    renderCell: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') return '';
      if (typeof params.value === 'number') return params.value;
      return imputedValueCheck(params.value);
    },
  },

  // CAC (Central Air Conditioning)
  CentralAir: {
    editable: true,
    disableColumnMenu: true,
    field: "CentralAir",
    headerName: "CAC",
    type: "singleSelect",
    valueOptions: ['True', 'False'],
    minWidth: 60,
    align: 'center',
    headerAlign: 'center',
    flex: 0.5,
    valueGetter: (value) => {
      if (value === 1 || value === '1' || value === true || value === 'True') return 'True';
      if (value === 0 || value === '0' || value === false || value === 'False') return 'False';
      return '';
    },
    renderCell: (params) => {
      const v = params.value;
      if (!v || v === '') return <span></span>;
      return <span>{v}</span>;
    },
  },

  // PropertyAssessment
  PropertyAssessment: {
    editable: true,
    disableColumnMenu: true,
    field: "PropertyAssessment",
    headerName: "Assessment",
    type: "number",
    flex: 1,
    valueGetter: (value) => {
      // Handle empty/null/undefined values - return empty string
      if (value === null || value === undefined || value === '') {
        return '';
      }
      const numValue = Number(value);
      // Handle NaN or 0 values - return empty string for display consistency
      return isNaN(numValue) ? '' : numValue === 0 ? '' : numValue.toLocaleString();
    },
  },

  // Taxes
  Taxes: {
    editable: true,
    disableColumnMenu: true,
    field: "Taxes",
    headerName: "Taxes",
    type: "number",
    flex: 0.6,
    valueGetter: (value) => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      const numValue = Number(String(value).replace(/,/g, ''));
      return isNaN(numValue) ? '' : numValue.toLocaleString();
    },
  },

  // PrevAssessment
  PrevAssessment: {
    editable: false,
    disableColumnMenu: true,
    field: "PrevAssessment",
    headerName: "Prev. Assessment",
    type: "number",
    flex: 1,
    valueGetter: (value) => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      const numValue = Number(value);
      return isNaN(numValue) ? '' : numValue === 0 ? '' : numValue.toLocaleString();
    },
  },

  // LastDataUpdate
  LastDataUpdate: {
    editable: false,
    disableColumnMenu: true,
    field: "LastDataUpdate",
    headerName: "Inv. Update",
    type: "date",
    align: "center",
    flex: 0.8,
    valueGetter: (value) => {
      if (!value) return null;
      const parsedDate = new Date(value);
      return isNaN(parsedDate) ? null : parsedDate;
    },
  },

  // IFMV
  IFMV: {
    editable: false,
    disableColumnMenu: true,
    field: "IFMV",
    headerName: "IFMV",
    type: "number",
    flex: 0.8,
    renderCell: (params) => {
      const value = params.value;
      if (value === "" || value == null) {
        return "";
      }
      if (!isNaN(value)) {
        return Number(Number(value).toFixed(0)).toLocaleString() || "";
      }
      return "";
    }
  },

  // adj_IFMV (conditional based on run type)
  adj_IFMV: {
    editable: false,
    disableColumnMenu: true,
    field: "adj_IFMV",
    headerName: "Adj. IFMV",
    type: "number",
    align: "right",
    flex: 0.8,
    valueGetter: (value) => {
      if (value !== '' && value !== 0) { 
        return Math.round(Number(value));
      }
      return '';
    },
    renderCell: (cellParams) => {
      const ifmv = cellParams.row.IFMV || 0;
      const baseValue = ifmv;
      
      // Get misc adjustments from passed parameters
      const adjustments = Object.keys(params.memoizedSelectedAdjustments || {}).length > 0
        ? params.memoizedSelectedAdjustments
        : params.caseObject?.MiscAdjustments || {};
      const savedComps = params.savedComps || [];
      const { parcelID, currentCompId, parcelIndex, memoizedCompObject } = sheetIndexAndCompId(params, cellParams.row);
      
      // Use unified adjustment calculation function with IFMV as base price
      const result = getCompAdjustedPriceWithTooltip({
        subject: {}, // Not used in array-based mode
        comp: {}, // Not used in array-based mode
        salePrice: ifmv, // Pass IFMV as the sale price parameter
        miscAdjustments: adjustments,
        savedComps: savedComps,
        currentCompId,
        basePrice: baseValue,
        memoizedCompObject: memoizedCompObject,
        parcelIndex: parcelIndex,
        includeTooltipData: true,
        logType: 'TABLE_RENDERCELL'
      });

      const {
        finalAdjustedValue,
        rawAdjustments,
        miscAdjustmentTooltips,
        totalAdjustments
      } = result;

      // Format the value with appropriate sign and color
      const formattedValue = finalAdjustedValue ? Math.round(Number(finalAdjustedValue)).toLocaleString() : '';
      const rawValue = Number(ifmv).toLocaleString() || 0;

      return (
        <AdjustmentCell
          parcelID={parcelID}
          baseValue={baseValue}
          finalAdjustedValue={finalAdjustedValue}
          rawAdjustments={rawAdjustments}
          miscAdjustmentTooltips={miscAdjustmentTooltips}
          totalAdjustments={totalAdjustments}
          onAddAdjustment={params.handleAddAdj}
          onDeleteAdjustment={params.handleDeleteAdj}
          displayValue={formattedValue}
          rawValue={rawValue}
        />
      );
    },
  },

  // SalePrice (conditional based on run type)
  SalePrice: {
    editable: false,
    disableColumnMenu: true,
    field: "SalePrice",
    headerName: "Sale Price",
    type: "number",
    align: "right",
    flex: 0.8,
    renderCell: (params) => params.value ? Number(params.value).toLocaleString() : '',
  },

  // LOA (Sale Price / Assessment)
  LOA: {
    editable: false,
    disableColumnMenu: true,
    field: "LOA",
    headerName: "LOA",
    type: "number",
    align: "right",
    flex: 0.6,
    renderCell: (params) => {
      if (params.value === '' || params.value == null || isNaN(params.value)) {
        return '';
      }
      return `${Number(params.value).toFixed(2)}%`;
    },
  },

  CalculatedTAV: {
    editable: false,
    disableColumnMenu: true,
    field: 'CalculatedTAV',
    headerName: 'Calculated TAV',
    type: 'number',
    align: 'right',
    flex: 0.9,
    renderHeader: (headerParams) => (
      <Tooltip title="Sale price × RAR for the RAR year implied by sale date vs eval date (prior 1 year of sales from eval uses that tax year's RAR)">
        <span>
          <GridColumnHeaderTitle label="Calculated TAV" columnWidth={headerParams.colDef.computedWidth} />
        </span>
      </Tooltip>
    ),
    valueGetter: (_value, row) => {
      const { tav } = calculateCalculatedTav({
        salePrice: row?.SalePrice,
        saleDate: row?.SaleDate,
        evalDate: params.getEvalDate,
        taxYear: params.taxYear,
        rarHistory: params.rarHistory,
      });
      return tav;
    },
    renderCell: (cellParams) => {
      const calc = calculateCalculatedTav({
        salePrice: cellParams.row?.SalePrice,
        saleDate: cellParams.row?.SaleDate,
        evalDate: params.getEvalDate,
        taxYear: params.taxYear,
        rarHistory: params.rarHistory,
      });
      if (calc.tav == null && !cellParams.row?.SalePrice && !cellParams.row?.SaleDate) return '';
      const tooltip = (
        <div>
          <div>Sale Price: {calc.price != null ? calc.price.toLocaleString() : '—'}</div>
          <div>Sale Date: {calc.saleDateLabel}</div>
          <div>Eval Date: {calc.evalDateLabel}</div>
          <div>RAR Year: {calc.rarYear ?? '—'}</div>
          <div>RAR: {calc.rar != null ? `${calc.rar} (${(calc.rar * 100).toFixed(2)}%)` : '—'}{calc.isProjected ? ' (Projected)' : ''}</div>
          <div>
            Calc: {calc.price != null && calc.rar != null
              ? `${calc.price.toLocaleString()} × ${calc.rar} = ${calc.tav != null ? calc.tav.toLocaleString() : '—'}`
              : '—'}
          </div>
          {calc.missing.length > 0 && <div>Missing: {calc.missing.join(', ')}</div>}
        </div>
      );
      return (
        <div className="flex items-center justify-end gap-0.5 h-full w-full">
          <span>{calc.tav != null ? `${calc.tav.toLocaleString()} AV` : ''}</span>
          <Tooltip title={tooltip} placement="right">
            <InfoOutlinedIcon
              sx={{ fontSize: 14, color: 'inherit', cursor: 'pointer', flexShrink: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </div>
      );
    },
  },

  // SaleDate (conditional based on run type)
  SaleDate: {
    editable: false,
    disableColumnMenu: true,
    field: "SaleDate",
    headerName: "Sale Date",
    type: "date",
    align: "center",
    flex: 0.9,
    valueGetter: (value) => parseCalendarDate(value),
  },

  // ContractDate (conditional based on run type)
  ContractDate: {
    editable: false,
    disableColumnMenu: true,
    field: "ContractDate",
    headerName: "Contract Date",
    type: "date",
    align: "center",
    flex: 0.6,
    valueGetter: (value) => parseCalendarDate(value),
  },

  // MLSNumber (conditional based on run type)
  MLSNumber: {
    editable: false,
    disableColumnMenu: true,
    field: "MLSNumber",
    headerName: "MLS Number",
    type: "number",
    align: "right",
    flex: 0.7,
    valueGetter: (value) => {
      if(value==='None'){
        return '';
      }
      return value || '';
    },
  },

  // adj_price (conditional based on run type)
  adj_price: {
    editable: false,
    disableColumnMenu: true,
    field: "adj_price",
    headerName: "Adj Sale",
    type: "number",
    align: "right",
    flex: 0.8,
    valueGetter: (value) => {
      if (value !== '' && value !== 0) { 
        return Math.round(Number(value));
      }
      return '';
    },
    renderCell: (cellParams) => {
      const salePrice = cellParams.row.SalePrice || 0;
      const baseValue = salePrice;
      
      // Get misc adjustments from passed parameters
      const adjustments = Object.keys(params.memoizedSelectedAdjustments || {}).length > 0
        ? params.memoizedSelectedAdjustments
        : params.caseObject?.MiscAdjustments || {};
      const savedComps = params.savedComps || [];
      const { parcelID, currentCompId, parcelIndex, memoizedCompObject } = sheetIndexAndCompId(params, cellParams.row);

      // Use unified adjustment calculation function
      const result = getCompAdjustedPriceWithTooltip({
        // should extract parcel id in this unified function
        subject: {}, // Not used in array-based mode
        comp: {}, // Not used in array-based mode
        salePrice: salePrice,
        miscAdjustments: adjustments,
        savedComps: savedComps,
        currentCompId,
        basePrice: baseValue,
        memoizedCompObject: memoizedCompObject,
        parcelIndex: parcelIndex,
        includeTooltipData: true,
        logType: 'TABLE_RENDERCELL'
      });

      const {
        finalAdjustedValue,
        rawAdjustments,
        miscAdjustmentTooltips,
        totalAdjustments
      } = result;

      // Format the value with appropriate sign and color
      const formattedValue = finalAdjustedValue ? Math.round(Number(finalAdjustedValue)).toLocaleString() : '';
      const rawValue = Number(salePrice).toLocaleString() || 0;

      return (
        <AdjustmentCell
          parcelID={parcelID}
          baseValue={baseValue}
          finalAdjustedValue={finalAdjustedValue}
          rawAdjustments={rawAdjustments}
          miscAdjustmentTooltips={miscAdjustmentTooltips}
          totalAdjustments={totalAdjustments}
          onAddAdjustment={params.handleAddAdj}
          onDeleteAdjustment={params.handleDeleteAdj}
          displayValue={formattedValue}
          rawValue={rawValue}
        />
      );
    },
  },

  // be_adj_price (conditional based on run type)
  be_adj_price: {
    field: "be_adj_price",
    editable: false,
    headerName: "BE Adj Price",
    type: "number",
    flex: 0.8,
    hide: true,
  },

  be_adj_IFMV: {
    field: "be_adj_IFMV",
    editable: false,
    headerName: "BE Adj IFMV",
    type: "number",
    flex: 0.8,
    hide: true,
  },

  gross_adj_pct: {
    editable: false,
    disableColumnMenu: true,
    field: "gross_adj_pct",
    headerName: "% Gross Adj",
    type: "number",
    align: "right",
    flex: 0.8,
    sortable: true,
    valueGetter: (value, row) => {
      const { parcelID, currentCompId, parcelIndex, memoizedCompObject } = sheetIndexAndCompId(params, row);
      const baseValue = parseAdjustmentBaseValue(params.IS_SALE_RUN ? row.SalePrice : row.IFMV);
      if (!parcelID || !baseValue) return null;
      const adjustments = Object.keys(params.memoizedSelectedAdjustments || {}).length > 0
        ? params.memoizedSelectedAdjustments
        : params.caseObject?.MiscAdjustments || {};
      const savedComps = params.savedComps || [];
      if (parcelIndex === -1) return null;
      const result = getCompAdjustedPriceWithTooltip({
        subject: {},
        comp: {},
        salePrice: baseValue,
        miscAdjustments: adjustments,
        savedComps,
        currentCompId,
        basePrice: baseValue,
        memoizedCompObject,
        parcelIndex,
        includeTooltipData: true,
        logType: 'TABLE_RENDERCELL'
      });
      return calculateAdjustmentPercentages(result, baseValue).grossAdjPct;
    },
    renderCell: (cellParams) => {
      const value = Number(cellParams.value);
      if (!Number.isFinite(value)) return '';
      return `${value.toFixed(1)}%`;
    },
  },

  net_adj_pct: {
    editable: false,
    disableColumnMenu: true,
    field: "net_adj_pct",
    headerName: "% Net Adj",
    type: "number",
    align: "right",
    flex: 0.8,
    sortable: true,
    valueGetter: (value, row) => {
      const { parcelID, currentCompId, parcelIndex, memoizedCompObject } = sheetIndexAndCompId(params, row);
      const baseValue = parseAdjustmentBaseValue(params.IS_SALE_RUN ? row.SalePrice : row.IFMV);
      if (!parcelID || !baseValue) return null;
      const adjustments = Object.keys(params.memoizedSelectedAdjustments || {}).length > 0
        ? params.memoizedSelectedAdjustments
        : params.caseObject?.MiscAdjustments || {};
      const savedComps = params.savedComps || [];
      if (parcelIndex === -1) return null;
      const result = getCompAdjustedPriceWithTooltip({
        subject: {},
        comp: {},
        salePrice: baseValue,
        miscAdjustments: adjustments,
        savedComps,
        currentCompId,
        basePrice: baseValue,
        memoizedCompObject,
        parcelIndex,
        includeTooltipData: true,
        logType: 'TABLE_RENDERCELL'
      });
      return calculateAdjustmentPercentages(result, baseValue).netAdjPct;
    },
    renderCell: (cellParams) => {
      const value = Number(cellParams.value);
      if (!Number.isFinite(value)) return '';
      return `${value.toFixed(1)}%`;
    },
  },

  // time_adj (conditional based on run type)
  time_adj: {
    editable: false,
    disableColumnMenu: false,
    field: "time_adj",
    headerName: "Time Adj",
    type: "number",
    align: "right",
    sortable: false,
    flex: 0.6,
    valueGetter: (value) => {
      if (value !== '') { 
        return Math.round(Number(value));
      }
      return 'Set Value';
    },
    renderCell: (cellParams) => {
      return (
        <TimeAdjustmentCell
          subject={cellParams.row}
          getTimeAdjustmentSettings={params.getTimeAdjustmentSettings}
          IS_SALE_RUN={params.IS_SALE_RUN}
        />
      );
    },
  },

  // Distance
  Distance: {
    editable: false,
    disableColumnMenu: true,
    field: "Distance",
    headerName: "Dist (Mi)",
    type: "number",
    flex: 0.6,
    valueGetter: (value) => {
      if (value === 0 || value === '0' || value === '') {
        return 0;
      }
      if (!isNaN(value) && value !== '' && value !== null && value !== undefined) {
        return Number(Number(value).toFixed(2));
      }
      return '';
    },
    renderCell: (params) => {
      if (params.value === 0) {
        return '0';
      }
      return params.value;
    }
  },
});

// Main column definitions function
export const createColumnDefinitions = ({
  handleSelectAllChange,
  handleAddressClick,
  IS_SALE_RUN = false,
  getTimeAdjustmentSettings,
  getEvalDate,
  taxYear,
  rarHistory,
  memoizedSelectedAdjustments,
  caseObject,
  getAdjustments,
  savedComps,
  memoizedCompObject,
  handleAddAdj,
  handleDeleteAdj,
  hasSelectedComps = false,
}) => {
  // Create column definitions with parameters
  const columnDefinitions = createColumnDefinitionsObject({
    handleSelectAllChange,
    handleAddressClick,
    IS_SALE_RUN,
    getTimeAdjustmentSettings,
    getEvalDate,
    taxYear,
    rarHistory,
    memoizedSelectedAdjustments,
    caseObject,
    getAdjustments,
    savedComps,
    memoizedCompObject,
    handleAddAdj,
    handleDeleteAdj,
    hasSelectedComps,
  });

  const specByField = new Map(CASE_REVIEW_COLUMNS.map((col) => [col.field, col]));

  // Filter out conditional columns based on run type.
  // A mapping entry with headerName and no custom def becomes a plain column.
  const filteredColumnOrder = COLUMN_ORDER.filter(columnKey => {
    const spec = specByField.get(columnKey);
    if (!columnDefinitions[columnKey] && !spec?.headerName) return false;

    if (['adj_IFMV', 'be_adj_IFMV'].includes(columnKey) && IS_SALE_RUN) return false;
    if (['adj_price', 'be_adj_price', 'time_adj'].includes(columnKey) && !IS_SALE_RUN) return false;

    return true;
  });

  return filteredColumnOrder.map((columnKey) => {
    if (columnDefinitions[columnKey]) return columnDefinitions[columnKey];
    const spec = specByField.get(columnKey);
    return {
      editable: false,
      disableColumnMenu: true,
      field: spec.field,
      headerName: spec.headerName,
      type: 'string',
      align: 'left',
      hide: !!spec.hidden,
      flex: spec.flex ?? 0.9,
    };
  });
};
